import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
/** Wie oft das Oeffnen des Drawers hoechstens wiederholt wird, wenn
 *  <theme-drawer> beim ersten Seitenbesuch noch nicht hochgefahren ist. */
const OPEN_RETRIES = 5;

class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {HTMLElement | null} */
  #boundScroller = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /**
   * CM: Zeitpunkt, ab dem der Platzhalter sichtbar war.
   * @type {number}
   */
  #primingSince = 0;

  /** @type {number} */
  #revealTimer = 0;

  /**
   * CM: Notbremse-Timer fuer den Platzhalter. Ersetzt die fruehere
   * requestAnimationFrame-Schleife in #clearPending.
   * @type {number}
   */
  #primingFallback = 0;

  /**
   * CM: TIMING
   * ------------------------------------------------------------------
   * PRIMING_MIN_MS  Mindeststandzeit des Platzhalters.
   *
   *                 Stand vorher auf 150ms und davor auf 280ms. Die
   *                 Begruendung damals: kommt die Antwort sehr schnell,
   *                 springt der Inhalt noch waehrend des Reinfahrens um.
   *
   *                 Das war die Behandlung eines Symptoms. Ursache war das
   *                 harte Umschalten zwischen zwei voellig verschiedenen
   *                 Zustaenden (grauer Platzhalter -> fertiger Warenkorb).
   *                 Seit assets/cm-cart-engine.js bereits beim Klick die
   *                 echte Produktzeile einsetzt, gibt es diesen Sprung
   *                 nicht mehr: der Platzhalter laeuft nur noch, wenn der
   *                 Warenkorb vorher komplett leer war, und selbst dann
   *                 loest ihn eine weiche Blende ab.
   *
   *                 Eine Mindestwartezeit ist damit das, was sie immer war:
   *                 kuenstliche Verzoegerung. Deshalb 0.
   * REVEAL_MS       Dauer der weichen Einblendung des echten Inhalts
   *                 (muss zur CSS-Animation cm-cart-reveal passen).
   */
  static PRIMING_MIN_MS = 0;
  static REVEAL_MS = 120;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.addEventListener('cm:cart-sections-applied', this.#handleSectionsApplied);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.removeEventListener('cm:cart-sections-applied', this.#handleSectionsApplied);
    clearTimeout(this.#revealTimer);
    clearTimeout(this.#primingFallback);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    this.#boundScroller?.removeEventListener('scroll', this.#updateScrollState);
    this.#boundScroller = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#bindScroller();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    // CM: IMMER oeffnen.
    // ------------------------------------------------------------------
    // Frueher hing das an this.hasAttribute('auto-open'), also an der
    // Theme-Einstellung "Warenkorb automatisch oeffnen". Wird die im
    // Customizer versehentlich abgeschaltet, bekommt der Kunde nach dem
    // Add-to-cart keinerlei Rueckmeldung mehr. Das Oeffnen ist jetzt fest
    // verdrahtet und laesst sich nicht mehr aus Versehen deaktivieren.
    //
    // Einzige verbleibende Bedingung: der Drawer ist noch nicht offen
    // (sonst wuerde die Slide-in-Animation mitten im Betrachten neu
    // starten) und es handelt sich wirklich um ein Hinzufuegen.
    const shouldAutoOpen = event.action === 'add' && !this.#themeDrawer?.isOpen;

    // CM: Der Drawer wartet nicht mehr auf die Server-Antwort.
    //
    // Vorher lag das Oeffnen komplett in event.promise.then(). Dieses Promise
    // loest erst auf, nachdem ZWEI Requests durch sind (cart/add.js und
    // danach der Section-Refresh). Auf Mobilfunk waren das schnell 600-900ms,
    // in denen nach dem Klick sichtbar nichts passierte.
    //
    // Jetzt faehrt das Panel sofort rein, der Inhalt wird vom normalen
    // Morphing nachgezogen. Waehrend der Wartezeit laeuft oben eine feine
    // Fortschrittslinie (CSS: cart-drawer-component[data-cart-pending]).
    //
    // Der Drawer faehrt sofort beim Klick rein, der Inhalt wird vom
    // normalen Morphing nachgezogen. Solange der Warenkorb vorher leer war,
    // laeuft dabei der Lade-Platzhalter des Themes (data-cart-priming), der
    // "Your cart is empty" verdeckt — siehe #clearPending weiter unten.
    const OPTIMISTIC_OPEN = true;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    const open = () => {
      // CM: Fix gegen "Add-to-cart tut manchmal gar nichts".
      // ------------------------------------------------------------------
      // this.#themeDrawer.open() ruft je nach Breakpoint panel.showModal()
      // bzw. panel.show() auf. Ist das native <dialog> aus irgendeinem
      // Grund schon offen (z.B. nach einer Wiederherstellung aus dem
      // Browser-Zurueck-Cache, wo die JS-Zustaende von <theme-drawer> und
      // das native <dialog> kurzzeitig auseinanderlaufen koennen), wirft
      // showModal() eine InvalidStateError - und zwar SYNCHRON, an dieser
      // Stelle, ausserhalb jedes try/catch. Ohne Abfangen wuerde das den
      // gesamten restlichen Handler abbrechen: der Code weiter unten, der
      // auf die Server-Antwort wartet (event.promise.then(...)), wuerde
      // nie erreicht. Das Produkt waere laengst im Warenkorb, der Drawer
      // bliebe aber zu und der Kunde saehe scheinbar gar keine Reaktion.
      //
      // CM (Nachtrag): EIN Wiederholungsversuch reichte nicht.
      // ------------------------------------------------------------------
      // Zweiter, seltener Fall beim ERSTEN Seitenbesuch: <theme-drawer> ist
      // im Markup da, aber assets/theme-drawer.js ist noch nicht
      // ausgefuehrt. Dann ist this.#themeDrawer zwar ein Element, aber
      // .open ist keine Funktion - der Aufruf wirft, und ein einzelner
      // Versuch im naechsten Frame trifft dieselbe Lage nochmal an.
      //
      // Deshalb jetzt: auf die Registrierung des Elements warten und
      // danach mehrfach mit wachsendem Abstand versuchen. Insgesamt rund
      // eine Sekunde - laenger waere fuer den Kunden ohnehin sinnlos.
      const attempt = (tries) => {
        const drawer = this.#themeDrawer;
        if (!drawer) return;
        if (drawer.isOpen) return;

        try {
          if (typeof drawer.open !== 'function') throw new Error('theme-drawer noch nicht hochgefahren');
          drawer.open();
          return;
        } catch (error) {
          if (tries <= 0) {
            console.error('[cart-drawer] Drawer liess sich nicht oeffnen:', error);
            return;
          }
          // 1. Wiederholung im naechsten Frame, danach in groesser
          // werdenden Abstaenden (60/120/240/480ms).
          if (tries === OPEN_RETRIES) {
            requestAnimationFrame(() => attempt(tries - 1));
          } else {
            setTimeout(() => attempt(tries - 1), 60 * Math.pow(2, OPEN_RETRIES - tries));
          }
        }
      };

      if (customElements.get('theme-drawer')) {
        attempt(OPEN_RETRIES);
      } else {
        // Element noch gar nicht registriert: erst darauf warten, parallel
        // trotzdem die Versuchsreihe starten (falls whenDefined haengt).
        customElements.whenDefined('theme-drawer').then(() => attempt(OPEN_RETRIES));
        attempt(OPEN_RETRIES);
      }
    };

    const settle = () =>
      requestAnimationFrame(() => {
        this.#updateStickyState();
        // Das Morphing kann den Scroll-Container ausgetauscht haben.
        this.#bindScroller();
        this.#updateScrollState();
      });

    // Fall A — sofort oeffnen, noch waehrend der Request laeuft.
    if (OPTIMISTIC_OPEN && shouldAutoOpen) {
      this.setAttribute('data-cart-pending', '');

      // War der Warenkorb bis eben leer, wuerde jetzt "Your cart is empty"
      // aufblitzen. Stattdessen laeuft ein Platzhalter, bis der echte
      // Inhalt eingemorpht ist.
      if (this.querySelector('.cart-drawer--empty')) {
        this.#primingSince = performance.now();
        this.removeAttribute('data-cart-revealing');
        this.setAttribute('data-cart-priming', '');
      }

      if (sourceModal?.open) {
        // Quick-Add: erst das Modal schliessen lassen, damit dessen
        // Fokus-Wiederherstellung nicht mit unserer kollidiert.
        sourceModal.addEventListener('close', open, { once: true });
      } else {
        open();
      }
    }

    event.promise
      ?.then(({ detail }) => {
        this.#clearPending();

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        // Fall B — klassischer Weg (OPTIMISTIC_OPEN aus, oder der Drawer
        // konnte vorhin noch nicht geoeffnet werden).
        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            () => {
              open();
              settle();
            },
            { once: true }
          );
        } else {
          open();
          settle();
        }
      })
      .catch((error) => {
        this.#clearPending();
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * Nimmt Lade- und Platzhalter-Zustand zurueck.
   *
   * Der Platzhalter darf NICHT einfach beim Auflösen des Promise weg —
   * das Morphing haengt am selben Promise, und welcher Handler zuerst
   * laeuft, ist nicht garantiert. War der Platzhalter zuerst weg, blitzte
   * der leere Warenkorb genau dann doch noch durch.
   *
   * Deshalb wird pro Frame geprueft, ob die Klasse .cart-drawer--empty
   * verschwunden ist. Erst dann faellt der Platzhalter. Die Obergrenze von
   * rund einer Sekunde ist eine Notbremse: bleibt der Warenkorb wirklich
   * leer (Fehlerfall), haengt der Platzhalter nicht endlos fest.
   */
  #clearPending = () => {
    this.removeAttribute('data-cart-pending');

    if (!this.hasAttribute('data-cart-priming')) return;
    if (this.hasAttribute('data-cart-clearing')) return;
    this.setAttribute('data-cart-clearing', '');

    // CM: Die alte Fassung lief hier eine requestAnimationFrame-Schleife,
    // die bis zu 400ms lang jeden Frame nachgesehen hat, ob die Klasse
    // .cart-drawer--empty schon verschwunden ist — also 24 Durchlaeufe
    // Handarbeit, nur um auf ein Ereignis zu warten, das es laengst gibt.
    //
    // Zwei Ausloeser reichen und sind beide praezise:
    //   1. Der Inhalt ist da  -> .cart-drawer--empty ist weg. Das laesst
    //      sich im naechsten Frame nach dem Morph einmal pruefen; das
    //      Morphing selbst laeuft synchron im selben Tick.
    //   2. Es kommt nichts    -> Notbremse nach 600ms, damit der
    //      Platzhalter bei einem Serverfehler nicht haengen bleibt.
    const finish = () => {
      if (!this.hasAttribute('data-cart-clearing')) return;
      window.clearTimeout(this.#primingFallback);
      this.removeAttribute('data-cart-clearing');
      this.#reveal(!this.querySelector('.cart-drawer--empty'));
    };

    window.clearTimeout(this.#primingFallback);
    this.#primingFallback = window.setTimeout(finish, 600);

    requestAnimationFrame(() => {
      // Ist der Warenkorb weiterhin leer, war es ein Fehlerfall oder die
      // Antwort steht noch aus — dann uebernimmt die Notbremse oben.
      if (!this.querySelector('.cart-drawer--empty')) finish();
    });
  };

  /**
   * CM: Platzhalter weg, echten Inhalt weich einblenden.
   * @param {boolean} animate
   */
  #reveal(animate) {
    if (animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.setAttribute('data-cart-revealing', '');
      clearTimeout(this.#revealTimer);
      this.#revealTimer = window.setTimeout(() => {
        this.removeAttribute('data-cart-revealing');
      }, CartDrawerComponent.REVEAL_MS + 60);
    }

    this.removeAttribute('data-cart-priming');
    this.#updateStickyState();
    this.#bindScroller();
  }

  /**
   * CM: Der Drawer-Inhalt wurde (frueh) eingespielt – Ladezustand sofort
   * aufloesen, nicht erst wenn auch /cart.json durch ist.
   */
  #handleSectionsApplied = () => {
    if (!this.hasAttribute('data-cart-pending') && !this.hasAttribute('data-cart-priming')) return;
    this.#clearPending();
    requestAnimationFrame(() => {
      this.#updateStickyState();
      this.#bindScroller();
    });
  };

  /**
   * Blendet die weiche Kante ueber dem Checkout-Block nur ein, solange
   * unterhalb noch Inhalt liegt. Steht der Nutzer ganz unten, verschwindet
   * sie — sonst laege dauerhaft ein Schleier ueber dem letzten Element.
   */
  #updateScrollState = () => {
    const dialog = this.#dialog;
    const content = dialog?.querySelector('.cart-drawer__content');
    const scroller = /** @type {HTMLElement | null} */ (dialog?.querySelector('.cart-drawer__scroll'));

    if (!content || !scroller) return;

    const remaining = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    content.setAttribute('data-scroll-state', remaining > 4 ? 'more' : 'end');
  };

  /**
   * Haengt den Scroll-Listener an den aktuellen Scroll-Container.
   * Muss nach jedem Cart-Update erneut laufen, weil das Morphing den
   * Container austauschen kann.
   */
  #bindScroller = () => {
    const scroller = /** @type {HTMLElement | null} */ (
      this.#dialog?.querySelector('.cart-drawer__scroll')
    );

    if (!scroller) return;

    // Beim Morphing bleibt der Knoten oft derselbe, die Kinder darin nicht.
    // Der Listener wird deshalb nur bei echtem Wechsel neu gesetzt, die
    // Groessen-Beobachtung dagegen jedes Mal.
    if (scroller !== this.#boundScroller) {
      this.#boundScroller?.removeEventListener('scroll', this.#updateScrollState);
      this.#boundScroller = scroller;
      scroller.addEventListener('scroll', this.#updateScrollState, { passive: true });
    }

    // Der Scroller selbst aendert seine Groesse kaum — der Inhalt darin schon
    // (Upsell laedt nach, Reward-Row waechst). Deshalb beide beobachten.
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(() => this.#updateScrollState());
    this.#resizeObserver.observe(scroller);
    for (const child of scroller.children) this.#resizeObserver.observe(child);

    this.#updateScrollState();
  };

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
